import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { Ledger } from '../ledger.types';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { TrackedFieldsService } from 'ldt-ledger-service-api-v2';
import { Log } from 'src/app/logger.service';

interface TrackedFieldOption {
  key: string;
  displayName: string;
  group: string;
}

@Component({
    selector: 'app-ledger-tracked-fields',
    templateUrl: './ledger-tracked-fields.component.html',
    styleUrls: ['./ledger-tracked-fields.component.scss'],
    standalone: false
})
export class LedgerTrackedFieldsComponent implements OnInit {
  @Input() ledger: Ledger;
  @Input() orgId: string;

  allFields: TrackedFieldOption[] = [
    { key: 'name', displayName: 'Name', group: 'General' },
    { key: 'location', displayName: 'Location', group: 'General' },
    { key: 'country', displayName: 'Country', group: 'General' },
    { key: 'linkedin', displayName: 'LinkedIn Profile', group: 'General' },
    { key: 'is_verified', displayName: 'Is Verified', group: 'General' },
    { key: 'has_multiple_jobs', displayName: 'Has Multiple Jobs', group: 'General' },
    { key: 'relative_job.title', displayName: 'Job Title', group: 'Current Job' },
    { key: 'relative_job.email', displayName: 'Job Email', group: 'Current Job' },
    { key: 'relative_job.email_status', displayName: 'Job Email Status', group: 'Current Job' },
    { key: 'relative_job.location', displayName: 'Job Location', group: 'Current Job' },
    { key: 'relative_job.started_at', displayName: 'Job Started At', group: 'Current Job' },
    { key: 'relative_job.company.name', displayName: 'Company Name', group: 'Company' },
    { key: 'relative_job.company.domain', displayName: 'Company Domain', group: 'Company' },
    {
      key: 'relative_job.company.employee_count',
      displayName: 'Company Employee Count',
      group: 'Company',
    },
    { key: 'relative_job.company.industry', displayName: 'Company Industry', group: 'Company' },
    { key: 'relative_job.company.linkedin', displayName: 'Company LinkedIn', group: 'Company' },
    { key: 'relative_job.company.location', displayName: 'Company Location', group: 'Company' },
  ];
  selectedFields: Set<string> = new Set<string>();
  groupedFields: { groupName: string; fields: TrackedFieldOption[] }[] = [];
  currentVersion: string = '';

  constructor(
    private notify: NotificationService,
    private route: ActivatedRoute,
    public darkModeService: DarkModeService,
    private trackedFieldsService: TrackedFieldsService
  ) {}

  ngOnInit(): void {
    // Get the ledger from the resolver and org ID from the route
    if (this.route.parent?.snapshot.data.userdata) {
      this.ledger = this.route.parent.snapshot.data.userdata;
    }

    let routeOrg = this.route.parent?.snapshot.paramMap.get('orgId');
    if (routeOrg) {
      this.orgId = routeOrg;
    }

    if (!this.orgId || !this.ledger || !this.ledger.id) {
      this.notify.error(
        'Required information (Org ID or Ledger ID) is missing. Cannot load tracked fields.'
      );
      Log.s('OrgID or Ledger ID is missing.', 'error', {
        orgId: this.orgId,
        ledgerId: this.ledger.id,
      });
      return;
    }

    this.refreshData();
    this.prepareGroupedFields();
  }

  prepareGroupedFields(): void {
    const groups: { [key: string]: TrackedFieldOption[] } = {};
    this.allFields.forEach((field) => {
      groups[field.group] = groups[field.group] || [];
      groups[field.group].push(field);
    });
    this.groupedFields = Object.keys(groups).map((groupName) => ({
      groupName,
      fields: groups[groupName],
    }));
  }

  refreshData() {
    if (!this.orgId || !this.ledger?.id) {
      Log.s('OrgID or Ledger ID is missing for tracked fields refresh.', 'error', {
        orgId: this.orgId,
        ledgerId: this.ledger.id,
      });
      return;
    }
    this.trackedFieldsService.getTrackedFields(this.orgId, this.ledger.id).subscribe({
      next: (res) => {
        this.selectedFields = new Set(res.tracked_fields);
        // tracked_fields_version became nullable in ledger-service-api 1.0.31+
        this.currentVersion = res.tracked_fields_version || '';
      },
      error: (err) => {
        this.notify.error('Oops. There was an error getting the data. Please try again later.');
        Log.s('Error getting tracked fields.', 'error', {
          orgId: this.orgId,
          ledgerId: this.ledger.id,
          error: err,
        });
      },
    });
  }

  isSelected(fieldKey: string): boolean {
    return this.selectedFields.has(fieldKey);
  }

  toggleFieldSelection(fieldKey: string): void {
    if (this.selectedFields.has(fieldKey)) {
      this.selectedFields.delete(fieldKey);
    } else {
      this.selectedFields.add(fieldKey);
    }
  }

  onUpdate(): void {
    if (!this.orgId || !this.ledger?.id) {
      this.notify.error('Cannot update. Missing Org ID or Ledger ID.');
      Log.s('OrgID or Ledger ID is missing for tracked fields update.', 'error', {
        orgId: this.orgId,
        ledgerId: this.ledger.id,
      });

      return;
    }
    const selectedFieldKeys = Array.from(this.selectedFields);

    this.trackedFieldsService
      .updateTrackedFields(this.orgId, this.ledger.id, { tracked_fields: selectedFieldKeys })
      .subscribe({
        next: () => {
          this.notify.success('Tracked fields updated successfully!');
        },
        error: (err) => {
          this.notify.error(
            'Oops. There was an error updating the fields. Please try again later.'
          );
          Log.s('Error updating tracked fields:', 'error', {
            orgId: this.orgId,
            ledgerId: this.ledger.id,
            error: err,
          });
        },
      });
  }
}
